import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowUp, ArrowDown, GripVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Stage = {
  id: number
  pipeline: number
  name: string
  description: string
  order: number
  color: string
  is_won: boolean
  is_lost: boolean
  application_count: number
}

type Pipeline = {
  id: number
  name: string
  description: string
  is_default: boolean
  is_active: boolean
  stages: Stage[]
  stage_count: number
  jobs_in_use: number
  has_won_stage: boolean
  has_lost_stage: boolean
}

const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const KIND_COLOR: Record<'OPEN' | 'WON' | 'LOST', string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-700',
}

function stageKind(s: Stage): 'OPEN' | 'WON' | 'LOST' {
  if (s.is_won) return 'WON'
  if (s.is_lost) return 'LOST'
  return 'OPEN'
}

type TabKey = 'details' | 'stages'

// ─────────────────── Page ───────────────────
export default function ATSPipelineDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const pipelineId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const pipelineQ = useQuery({
    queryKey: ['ats', 'pipeline', pipelineId],
    queryFn: async () => (await api.get<Pipeline>(`/ats/pipelines/${pipelineId}/`)).data,
    enabled: !!pipelineId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Pipeline>) =>
      (await api.patch(`/ats/pipelines/${pipelineId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipeline', pipelineId] })
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
    },
  })

  const setDefaultMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/ats/pipelines/${pipelineId}/set-default/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['ats', 'pipeline', pipelineId] })
      toast.success('Set as default pipeline')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/ats/pipelines/${pipelineId}/`),
    onSuccess: () => {
      toast.success('Pipeline deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      navigate('/admin/ats/pipelines')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('stages')

  if (pipelineQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (pipelineQ.isError || !pipelineQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Pipeline not found.</p>
          <Link to="/admin/ats/pipelines" className="text-violet-600 hover:underline">← Back to pipelines</Link>
        </div>
      </AdminLayout>
    )
  }

  const pipeline = pipelineQ.data
  const save = (payload: Partial<Pipeline>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  const incomplete = !pipeline.has_won_stage || !pipeline.has_lost_stage

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/ats/pipelines" className="text-muted-foreground hover:text-foreground hover:underline">
            ← Pipelines
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 flex-wrap">
              <InlineText value={pipeline.name} onSave={(v) => save({ name: v })} />
              {pipeline.is_default && (
                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">DEFAULT</span>
              )}
            </h2>
            <div className="text-sm text-muted-foreground">
              {pipeline.stage_count} stage{pipeline.stage_count === 1 ? '' : 's'}
              {' · '}
              {pipeline.jobs_in_use} job{pipeline.jobs_in_use === 1 ? '' : 's'} in use
              {pipeline.description && <span> · {pipeline.description}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={pipeline.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!pipeline.is_default && (
                  <DropdownMenuItem onClick={() => setDefaultMut.mutate()}>
                    Set as default
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={pipeline.jobs_in_use > 0}
                  onClick={() => pipeline.jobs_in_use === 0 && confirm(
                    `pipeline "${pipeline.name}"`,
                    () => deleteMut.mutate(),
                  )}
                  className={pipeline.jobs_in_use === 0 ? 'text-destructive' : ''}
                >
                  {pipeline.jobs_in_use === 0
                    ? 'Delete pipeline'
                    : `Delete (in use by ${pipeline.jobs_in_use} job${pipeline.jobs_in_use === 1 ? '' : 's'})`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {incomplete && (
        <div className="mb-4 px-4 py-3 rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          <span className="font-semibold">Pipeline is incomplete.</span>{' '}
          Add at least one
          {!pipeline.has_won_stage && <span> <strong>Won</strong></span>}
          {!pipeline.has_won_stage && !pipeline.has_lost_stage && ' and one'}
          {!pipeline.has_lost_stage && <span> <strong>Lost</strong></span>}
          {' '}stage so the hire / reject flow can close applications correctly.
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['stages', 'details'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-violet-600 text-violet-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'stages' && `Stages (${pipeline.stages.length})`}
              {t === 'details' && 'Details'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab pipeline={pipeline} onSave={save} />}
      {tab === 'stages' && <StagesTab pipeline={pipeline} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  pipeline, onSave,
}: {
  pipeline: Pipeline
  onSave: (payload: Partial<Pipeline>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Pipeline">
        <Row label="Name">
          <InlineText value={pipeline.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Description">
          <InlineText
            value={pipeline.description}
            multiline
            onSave={(v) => onSave({ description: v })}
            placeholder="When should this pipeline be used? (Engineering / Exec search / Default…)"
          />
        </Row>
        <Row label="Default">
          {pipeline.is_default ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">DEFAULT</span>
          ) : (
            <span className="text-sm text-muted-foreground">
              Use the kebab menu → "Set as default" to make this the default pipeline.
            </span>
          )}
        </Row>
        <Row label="Jobs using this pipeline">
          {pipeline.jobs_in_use > 0 ? (
            <Link to={`/admin/ats/jobs?pipeline=${pipeline.id}`} className="text-violet-600 hover:underline text-sm">
              {pipeline.jobs_in_use} job{pipeline.jobs_in_use === 1 ? '' : 's'} →
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground">None</span>
          )}
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Stages tab ───────────────────

type StageDraft = {
  name: string
  description: string
  is_won: boolean
  is_lost: boolean
  color: string
}

function emptyStageDraft(): StageDraft {
  return {
    name: '',
    description: '',
    is_won: false,
    is_lost: false,
    color: '#94a3b8',
  }
}

function StagesTab({ pipeline }: { pipeline: Pipeline }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<StageDraft>(emptyStageDraft())

  function openCreate() {
    setDraft(emptyStageDraft())
    setCreating(true)
  }

  const stages = [...pipeline.stages].sort(
    (a, b) => a.order - b.order || a.id - b.id,
  )

  const createMut = useMutation({
    mutationFn: async () => {
      // New stage goes at the end of the existing order.
      const nextOrder = stages.length
        ? Math.max(...stages.map((s) => s.order)) + 10
        : 10
      return (await api.post('/ats/stages/', {
        ...draft,
        pipeline: pipeline.id,
        order: nextOrder,
      })).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['ats', 'pipeline', pipeline.id] })
      toast.success('Stage added')
      setCreating(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Stage> }) =>
      (await api.patch(`/ats/stages/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['ats', 'pipeline', pipeline.id] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Stage>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ats/stages/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['ats', 'pipeline', pipeline.id] })
      toast.success('Stage deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Swap orders with the neighbour at idx +/- delta.
  async function move(idx: number, delta: number) {
    const target = idx + delta
    if (target < 0 || target >= stages.length) return
    const a = stages[idx]
    const b = stages[target]
    await Promise.all([
      inlineMut.mutateAsync({ id: a.id, payload: { order: b.order } }),
      inlineMut.mutateAsync({ id: b.id, payload: { order: a.order } }),
    ])
  }

  // Native HTML5 drag-and-drop. We only need to track which row is being
  // dragged + which row is the current hover target so we can style the
  // drop indicator. On drop we rewrite the affected `order` values to put
  // the dragged row at the target index.
  const [dragId, setDragId] = useState<number | null>(null)
  const [overId, setOverId] = useState<number | null>(null)

  async function reorderByDrop(fromId: number, toId: number) {
    if (fromId === toId) return
    const fromIdx = stages.findIndex((s) => s.id === fromId)
    const toIdx = stages.findIndex((s) => s.id === toId)
    if (fromIdx < 0 || toIdx < 0) return

    // Build the new ordering, then re-assign sequential `order` values
    // in steps of 10 so future inserts have room without renumbering.
    const next = stages.slice()
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)

    // Only push updates for stages whose order actually changed.
    const updates: Array<Promise<unknown>> = []
    next.forEach((s, i) => {
      const newOrder = (i + 1) * 10
      if (s.order !== newOrder) {
        updates.push(inlineMut.mutateAsync({ id: s.id, payload: { order: newOrder } }))
      }
    })
    await Promise.all(updates)
  }

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Candidates flow top-to-bottom through these stages. Drag the grip handle (or use ↑/↓) to reorder. Mark exactly one stage as <strong>Won</strong> (Hired) and at least one as <strong>Lost</strong> (Rejected / Withdrawn) — the hire and reject flows depend on them.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add stage</Button>
      </div>

      {stages.length === 0 ? (
        <div className="bg-card rounded border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          No stages yet. Add one above — typical first stages: Sourced → Phone screen → Interview → Offer → Hired / Rejected.
        </div>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th className="w-20">Order</Th>
                <Th>Name</Th>
                <Th>Kind</Th>
                <Th>Color</Th>
                <Th className="text-right">Active apps</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {stages.map((s, idx) => (
                <tr
                  key={s.id}
                  draggable
                  onDragStart={() => setDragId(s.id)}
                  onDragOver={(e) => { e.preventDefault(); if (dragId !== null) setOverId(s.id) }}
                  onDragLeave={() => { if (overId === s.id) setOverId(null) }}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (dragId !== null) reorderByDrop(dragId, s.id)
                    setDragId(null); setOverId(null)
                  }}
                  onDragEnd={() => { setDragId(null); setOverId(null) }}
                  className={
                    dragId === s.id
                      ? 'opacity-40'
                      : overId === s.id
                        ? 'bg-violet-50'
                        : ''
                  }
                >
                  <Td className="text-xs">
                    <div className="flex items-center gap-1">
                      <span
                        className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                      <button
                        type="button"
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30"
                        onClick={() => move(idx, 1)}
                        disabled={idx === stages.length - 1}
                        title="Move down"
                      >
                        <ArrowDown className="h-3 w-3" />
                      </button>
                      <span className="font-mono text-muted-foreground ml-1">{s.order}</span>
                    </div>
                  </Td>
                  <Td className="font-medium">
                    <InlineText value={s.name} onSave={(v) => saveField(s.id, { name: v })} />
                  </Td>
                  <Td>
                    <InlineStatus
                      value={stageKind(s)}
                      colorMap={KIND_COLOR}
                      options={[
                        { value: 'OPEN', label: 'Open' },
                        { value: 'WON', label: 'Won (Hired)' },
                        { value: 'LOST', label: 'Lost (Rejected)' },
                      ]}
                      onSave={(v) => saveField(s.id, {
                        is_won: v === 'WON',
                        is_lost: v === 'LOST',
                      })}
                    />
                  </Td>
                  <Td>
                    <ColorEditor value={s.color} onSave={(v) => saveField(s.id, { color: v })} />
                  </Td>
                  <Td className="text-right text-sm">
                    {s.application_count > 0
                      ? <span className="text-violet-700 font-semibold">{s.application_count}</span>
                      : <Dash />}
                  </Td>
                  <Td className="text-right">
                    <RowActions
                      onEdit={() => { /* inline edits cover the row */ }}
                      onDelete={() => {
                        if (s.application_count > 0) {
                          toast.error(
                            `Move the ${s.application_count} active application${s.application_count === 1 ? '' : 's'} out of this stage before deleting it.`,
                          )
                          return
                        }
                        confirm(`stage "${s.name}"`, () => deleteMut.mutate(s.id))
                      }}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add stage"
        description="Add a stage to this pipeline. The stage lands at the end of the order — reorder it from the table afterwards."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Add stage"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="st-name">Name *</FieldLabel>
            <Input id="st-name" autoFocus required value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Phone screen / On-site / Offer" />
          </Field>
          <Field>
            <FieldLabel htmlFor="st-color">Color</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                id="st-color"
                type="color"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border"
              />
              <Input value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                className="font-mono" />
            </div>
            <FieldDescription>Used by the Kanban column headers.</FieldDescription>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_won}
                onChange={(e) => setDraft({ ...draft, is_won: e.target.checked, is_lost: false })} />
              Mark as <strong>Won</strong> stage (candidates here become Hired)
            </label>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_lost}
                onChange={(e) => setDraft({ ...draft, is_lost: e.target.checked, is_won: false })} />
              Mark as <strong>Lost</strong> stage (candidates here become Rejected)
            </label>
            <FieldDescription>A stage can be Won, Lost, or neither — not both.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Color editor ───────────────────

function ColorEditor({ value, onSave }: { value: string; onSave: (v: string) => Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  function openEditor() { setDraft(value); setOpen(true) }
  async function save() {
    if (draft === value) { setOpen(false); return }
    try { await onSave(draft); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <span
        className="inline-block w-5 h-5 rounded border cursor-pointer"
        style={{ backgroundColor: value || '#9ca3af' }}
        onClick={() => open ? setOpen(false) : openEditor()}
        title={value}
      />
      <span className="font-mono text-xs text-muted-foreground">{value || '—'}</span>
      {open && (
        <div className="ml-2 flex items-center gap-2 bg-popover border rounded p-2 shadow">
          <input
            type="color"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-10 cursor-pointer rounded"
          />
          <Input value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="font-mono h-7 text-xs w-28" />
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save}>Save</Button>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
